import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { PROTEOFORM_DEFAULT_THRESHOLD_CPM, smIconSize, TABLE_PAGE_SIZE_OPTIONS } from '@/constants';
import {
  apiFromAgGridReactRef,
  useCsvDownloadHandler,
  useInstrumentRunProteoformsCsvDownload,
  useRoles,
} from '@/hooks';
import { useProteoformResultsTableConfig } from '@/hooks/useAssays';
import type { parseFilterModel } from '@/lib/createAgGridDatasource';
import type { SortColumnState } from '@/lib/downloadOptions';
import { useSampleTableGrid } from '@/pages/Studies/components/useSampleTableGrid';
import SampleNameCell from '@/pages/Study/components/SampleNameCell';
import { tauEpitopeKeys } from '@/pages/Study/components/tauSamplesColDefs';
import { useCanOpenSampleDetail } from '@/pages/Study/components/useCanOpenSampleDetail';
import type { InstrumentRunPublic, SampleProteoformPublic } from '@/types';
import { flushColumnState } from '@/utils/flushColumnState';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Divider } from '@nautilusbio/nb-design-system/Divider';
import { Loading } from '@nautilusbio/nb-design-system/Loading';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import { Download } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../../styles.module.css';

import { buildRunSampleDetailsColDefs, runSampleDetailsSortFields } from './colDefs';
import { createRunSampleDetailsDatasource, type RunSampleDetailsStats } from './runSampleDetailsDatasource';
import { buildRunSampleDetailsDownloadOptions } from './runSampleDetailsDownloadOptions';
import type { RunSampleDetailsStatus } from './runSampleDetailsStatus';

interface Props {
  orgId: string;
  run: InstrumentRunPublic;
}

const PLACEHOLDER = '—';
const DEFAULT_PAGE_SIZE = 25;
const pageSizeKey = (runId: string) => `run-${runId}-sample-details-pageSize`;

const RunSampleDetailsSection = ({ orgId, run }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'sampleDetails' });
  const { t: tGeneral } = useTranslation('general');
  const downloadProteoforms = useInstrumentRunProteoformsCsvDownload(orgId, run.id);
  const gridRef = useRef<AgGridReact<SampleProteoformPublic>>(null);
  // useRoles('') falls back to the unscoped checks, whose hasRole would then compare undefined to undefined.
  const isSuperUser = useRoles(orgId).hasRole('super_user') && orgId !== '';
  const tableId = `run-sample-details-${run.id}`;

  const {
    data: tableConfig,
    isError: isTableConfigError,
    isPending: isTableConfigPending,
  } = useProteoformResultsTableConfig(run.assay_id);
  const thresholdCpm = tableConfig?.default_threshold_cpm ?? PROTEOFORM_DEFAULT_THRESHOLD_CPM;
  const epitopeKeys = useMemo(() => tauEpitopeKeys(tableConfig), [tableConfig]);
  const validSortFields = useMemo(() => runSampleDetailsSortFields(tableConfig), [tableConfig]);

  const buildDownloadOptions = useCallback(
    (columnState: readonly SortColumnState[], filterModel: Parameters<typeof parseFilterModel>[0]) =>
      buildRunSampleDetailsDownloadOptions(columnState, filterModel, tableConfig, thresholdCpm),
    [tableConfig, thresholdCpm],
  );
  const handleDownload = useCsvDownloadHandler(
    gridRef,
    apiFromAgGridReactRef,
    buildDownloadOptions,
    downloadProteoforms,
    t('download_failed'),
  );

  const [status, setStatus] = useState<RunSampleDetailsStatus>('loading');
  const [stats, setStats] = useState<RunSampleDetailsStats | null>(null);

  const datasource = useMemo(
    () =>
      createRunSampleDetailsDatasource({
        url: instrumentRunQueryKeys.endpoints.proteoformsSearch(orgId, run.id),
        thresholdCpm,
        validSortFields,
        epitopeKeys,
        decodeStatus: run.decode_status,
        onStats: setStats,
        onStatus: setStatus,
      }),
    [orgId, run.id, run.decode_status, thresholdCpm, epitopeKeys, validSortFields],
  );

  const {
    defaultColDef,
    handleFilterChanged,
    handleFirstDataRendered,
    handleGridReady,
    handleModelUpdated,
    handlePaginationChanged,
    pageSize,
  } = useSampleTableGrid<SampleProteoformPublic>({
    studyId: run.id,
    tableId,
    validSortFields,
    paginationPageSizeKeyFn: pageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
    autoHeight: true,
  });

  // Activated here rather than at creation, so a datasource from a render React discards never reports.
  useEffect(() => {
    datasource.setActive(true);
    // oxlint-disable-next-line set-state-in-effect -- the previous datasource's stats/status describe a different request; the new values only arrive via its async callbacks
    setStats(null);
    setStatus('loading');
    gridRef.current?.api?.setGridOption('serverSideDatasource', datasource);
    return () => datasource.setActive(false);
  }, [datasource]);

  const canOpenSample = useCanOpenSampleDetail();

  const colDefs = useMemo(
    () =>
      buildRunSampleDetailsColDefs({
        // i18next's strongly-typed t only accepts the known key union; colDefs takes plain strings.
        t: key => t(key as never),
        yes: tGeneral('yes'),
        no: tGeneral('no'),
        empty: tGeneral('empty'),
        tableConfig,
        sampleNameCellRenderer: (params: ICellRendererParams<SampleProteoformPublic>) => (
          <SampleNameCell canOpenSample={canOpenSample} data={params.data} />
        ),
        showCustomerVisibility: isSuperUser,
      }),
    [t, tGeneral, tableConfig, canOpenSample, isSuperUser],
  );

  const statusMessages: Record<Exclude<RunSampleDetailsStatus, 'loading' | 'ready'>, string> = {
    allWithheldQcFailure: t('status_all_withheld_qc_failure'),
    allWithheldQcPending: t('status_all_withheld_qc_pending'),
    noSamples: t('status_no_samples'),
    decodeNotStarted: t('status_decode_not_started'),
    decodePending: t('status_decode_pending'),
    decodeFailed: t('status_decode_failed'),
    noResults: t('status_no_results'),
    forbidden: t('status_forbidden'),
    notFound: t('status_not_found'),
    failed: t('status_failed'),
  };
  const statusMessage = status === 'loading' || status === 'ready' ? null : statusMessages[status];

  // A zero before decode (or with no samples) would read as a measured result, not an absent one.
  const hasMeasuredStats = status === 'ready' || status === 'noResults';
  const stat = (value: number | undefined) =>
    !hasMeasuredStats || value === undefined ? PLACEHOLDER : value.toLocaleString();

  // TODO: Restore after product verdict or delete
  //
  // Shown in the all-withheld states too, so a run with failed and pending samples keeps both counts visible.
  // const showsWithheldNotes =
  //   status === 'ready' ||
  //   status === 'noResults' ||
  //   status === 'allWithheldQcFailure' ||
  //   status === 'allWithheldQcPending';
  // const withheldNotes =
  //   showsWithheldNotes && stats
  //     ? [
  //         stats.numWithheldQcFailure > 0 && {
  //           key: 'qc_failure',
  //           text: t('withheld_qc_failure', { count: stats.numWithheldQcFailure }),
  //         },
  //         stats.numWithheldQcPending > 0 && {
  //           key: 'qc_pending',
  //           text: t('withheld_qc_pending', { count: stats.numWithheldQcPending }),
  //         },
  //       ].filter(note => note !== false)
  //     : [];
  // A zero-row result (nothing above threshold, or a filter matching nothing) would export a headers-only CSV.
  const canExport = tableConfig !== undefined && status === 'ready' && (stats?.total ?? 0) > 0;

  const statItems = [
    {
      key: 'proteoforms',
      label: t('stat_proteoforms'),
      value: stat(stats?.numProteoformGroups),
      testId: 'run-sample-details-stat-proteoforms',
    },
    { key: 'ptms', label: t('stat_ptms'), value: stat(stats?.numPtms), testId: 'run-sample-details-stat-ptms' },
    {
      key: 'isoforms',
      label: t('stat_isoforms'),
      value: stat(stats?.numIsoforms),
      testId: 'run-sample-details-stat-isoforms',
    },
    {
      key: 'threshold',
      label: t('stat_threshold'),
      value:
        tableConfig?.default_threshold_cpm !== null && tableConfig?.default_threshold_cpm !== undefined
          ? t('stat_threshold_value', { value: tableConfig.default_threshold_cpm.toLocaleString() })
          : PLACEHOLDER,
      testId: undefined,
    },
  ];

  const renderBody = () => {
    // The request's threshold, sort allowlist and boolean-filter parsing all come from the assay table config, so the grid waits for it.
    if (isTableConfigPending) return <Loading aria-label={t('loading')} type="inline" />;
    // A failed background refetch keeps the loaded config (TanStack Query v5), so only a first-load failure lacks it.
    if (isTableConfigError && !tableConfig) {
      return (
        <Text data-testid="run-sample-details-status" data-status="configUnavailable">
          {t('status_config_unavailable')}
        </Text>
      );
    }
    return (
      <>
        {/* TODO: Restore after product verdict or delete */}
        {/*{withheldNotes.map(note => (
          <Text data-testid="run-sample-details-withheld-note" key={note.key} size="sm">
            {note.text}
          </Text>
        ))}*/}
        {statusMessage !== null && (
          <Text data-status={status} data-testid="run-sample-details-status">
            {statusMessage}
          </Text>
        )}
        {status === 'failed' && (
          <Button
            data-testid="run-sample-details-retry"
            onPress={() => {
              const api = gridRef.current?.api;
              if (!api) return;
              setStatus('loading');
              api.refreshServerSide({ purge: true });
            }}
            visual="unstyled"
          >
            {t('retry')}
          </Button>
        )}
        <Table
          columnDefs={colDefs}
          ariaLabel={t('title')}
          // Hidden, not unmounted: its fetches are what update status. A transient failure keeps it
          // visible so filters and paging can recover.
          container={{
            'data-testid': 'run-sample-details-grid',
            hidden: statusMessage !== null && status !== 'failed',
          }}
          defaultColDef={defaultColDef}
          domLayout="autoHeight"
          enableAdvancedFilter={false}
          enableResponsiveResize={false}
          onFilterChanged={handleFilterChanged}
          onFirstDataRendered={handleFirstDataRendered}
          onGridPreDestroyed={e => flushColumnState(e.api, tableId)}
          onGridReady={handleGridReady}
          onModelUpdated={handleModelUpdated}
          onPaginationChanged={handlePaginationChanged}
          pagination
          paginationPageSize={pageSize}
          paginationPageSizeSelector={TABLE_PAGE_SIZE_OPTIONS}
          ref={gridRef}
          rowModelType="serverSide"
          tableId={tableId}
        />
      </>
    );
  };

  return (
    <Stack className={styles.sampleDetailsContainer} gap={3} orientation="vertical">
      <Stack className={styles.sampleDetailsTitleRow} orientation="horizontal">
        <Text as="h3">{t('title')}</Text>

        <Stack alignItems="center" className={styles.statsRow} gap={3} orientation="horizontal">
          <Button
            data-testid="run-sample-details-download"
            isDisabled={!canExport}
            isPending={downloadProteoforms.isPending}
            onPress={handleDownload}
            size="sm"
            visual="unstyled"
          >
            <Download size={smIconSize} />
            {t('download')}
          </Button>

          {statItems.map((item, index) => (
            <React.Fragment key={item.key}>
              {index > 0 && <Divider className={styles.statDivider} orientation="vertical" />}
              <Stack alignItems="center" gap={2} orientation="horizontal">
                <Text color="muted" noWrap size="sm">
                  {item.label}
                </Text>
                <Text data-testid={item.testId} noWrap size="sm" weight="semibold">
                  {item.value}
                </Text>
              </Stack>
            </React.Fragment>
          ))}
        </Stack>
      </Stack>

      {renderBody()}
    </Stack>
  );
};

export default RunSampleDetailsSection;
